<!-- Header -->
<tgx-onboarding-implementation-header [loggedUser]="loggedUser" [companyName]="companyName" [userImg]="userImg">
</tgx-onboarding-implementation-header>

@if (actualStep === -1) {
  <div class="container">
    <!-- Tools -->
    <div class="section mt-5">
      <p class="section__title">Useful tools before starting to certify</p>

      <div class="section__item border-bottom">
        <div class="row">
          <div class="col-1">
            <span class="section__item-ico d-flex justify-content-center h-100 align-items-center">
              <i class="fa-solid fa-handshake"></i>
            </span>
          </div>
          <div class="col-11">
            <p class="xen-heading-4 mb-0">Onboarding overview</p>
            <p class="section__item-subtit">
              A high-level overview of the onboarding process.
              <xen-link
                [size]="'medium'"
                [weight]="'regular'"
                [label]="'Read the documentation'"
                [href]="documentationLinks.onboarding.gettingStarted"
                [icon]="false"
                [theme]="'blue'"
                [target]="'_blank'"
                [underlined]="true"
              ></xen-link>
            </p>
          </div>
        </div>
      </div>

      <div class="section__item border-bottom">
        <div class="row">
          <div class="col-1">
            <span class="section__item-ico d-flex justify-content-center h-100 align-items-center">
              <i class="fa-solid fa-file-lines"></i>
            </span>
          </div>
          <div class="col-11">
            <p class="xen-heading-4 mb-0">HotelX API documentation</p>
            <p class="section__item-subtit">
              <xen-link
                [size]="'medium'"
                [weight]="'regular'"
                [label]="'Access the full technical documentation here'"
                [href]="documentationLinks.onboarding.hotelXQuickstart"
                [icon]="false"
                [theme]="'blue'"
                [target]="'_blank'"
                [underlined]="true"
              ></xen-link>
            </p>
            <p class="section__item-subtit">
              Find guides, code examples, and reference materials to help you integrate quickly and start sending
              requests efficiently. You will also find your Development Credentials to authenticate and begin testing
              your integration right away.
            </p>
          </div>
        </div>
      </div>

      <div class="section__item border-bottom">
        <div class="row">
          <div class="col-1 position-relative">
            <span class="section__item-ico d-flex justify-content-center h-100 align-items-center">
              <i class="fa-solid fa-file-lines"></i>
            </span>
            <span class="text">
              <xen-badge [type]="'label'" [status]="'buyer'" [theme]="'light'" [label]="'Buyer'"></xen-badge>
            </span>
          </div>
          <div class="col-11">
            <p class="xen-heading-4 mb-0">HotelX Essential for Buyers</p>

            <p class="section__item-subtit">
              Key technical concepts of the HotelX Pull Buyers API, including headers, endpoints, and performance tips.
              <xen-link
                [size]="'medium'"
                [weight]="'regular'"
                [label]="'Read the documentation'"
                [href]="documentationLinks.onboarding.hotelXQuickstart"
                [icon]="false"
                [theme]="'blue'"
                [target]="'_blank'"
                [underlined]="true"
              ></xen-link>
            </p>
          </div>
        </div>
      </div>

      <div class="section__item border-bottom">
        <div class="row">
          <div class="col-1">
            <img src="assets/img/pages/onboarding/bot_aina.svg" alt="Icon bot aina" />
          </div>
          <div class="col-11">
            <p class="xen-heading-4 mb-0">Ask our virtual assistant AIna</p>
            <p class="section__item-subtit mb-0">Got questions? Aina is here to help.</p>
            <p class="section__item-subtit">
              Our virtual assistant Aina is available 24/7 to answer your questions about integration, technical
              documentation, and more.
            </p>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-end mt-4 mb-5">
        <p class="xen-heading-5 mb-0 me-3">Are you ready to certificate?</p>
        <xen-btn
          [type]="'primary'"
          [size]="'large'"
          [label]="'Start certifications'"
          [icon]="true"
          [icon_placement]="'right'"
          [icon_snippet]="'far fa-angle-right'"
          [skin]="'blue'"
          (clicked)="nextStep()"
        ></xen-btn>
      </div>
    </div>
  </div>
}

@if (actualStep > -1 && actualStep < 4) {
  <div class="container">
    <!-- Progress bar -->
    <tgx-onboarding-implementation-progressbar-steps
      [steps]="steps"
      [progress]="progress"
    ></tgx-onboarding-implementation-progressbar-steps>

    @for (step of steps; track $index; let i = $index) {
      @if (step.active) {
        <div class="section mt-3">
          <div class="d-flex justify-content-between align-items-center mt-5">
            <p class="section__title">
              {{ step.title }}
              @if (step.mandatory) {
                <span>*</span>
              }
            </p>
          </div>

          @if (canInviteUsers && !isLoadingMembers) {
            @if (membersLoadFailed) {
              <xen-alert type="warning" class="mb-4" [dismiss]="false">
                <div class="d-flex align-items-center justify-content-between gap-3">
                  <span>We could not load the organization users. Inviting a user is temporarily unavailable.</span>
                  <xen-btn type="secondary" size="medium" label="Try again" (clicked)="retryMembersLoading()"></xen-btn>
                </div>
              </xen-alert>
            } @else {
              <xen-alert type="info" class="mb-4" [dismiss]="true">
                <div class="d-flex align-items-start">
                  <i class="fas fa-circle-info me-2 mt-1"></i>
                  <div class="d-flex flex-column">
                    <p class="mb-1 fw-bold">Not handling the implementation? Let someone else take care of it.</p>
                    <p class="mb-0">
                      If you're not going to handle the implementation, you can assign it to someone on the team using
                      the
                      <xen-link
                        [size]="'medium'"
                        [weight]="'regular'"
                        [label]="'invite a User'"
                        [theme]="'blue'"
                        (click)="inviteUser()"
                      ></xen-link>
                    </p>
                  </div>
                </div>
              </xen-alert>
            }
          }

          <!-- Certification-->
          @for (section of step.sections; track $index) {
            <div class="certification mb-5">
              <div class="row">
                <div class="col-5">
                  <p class="xen-heading-4 mb-0">
                    {{ section.title }}
                    @if (step.mandatory) {
                      <span>*</span>
                    }
                  </p>
                  <p class="section__item-subtit multiline-text">
                    {{ section.description }}
                  </p>
                  <p>
                    <xen-link
                      [size]="'medium'"
                      [weight]="'regular'"
                      [label]="'Read the documentation'"
                      [icon]="true"
                      [icon_snippet]="'fas fa-chevron-right'"
                      [icon_placement]="'right'"
                      [theme]="'blue'"
                      [target]="'_blank'"
                      [underlined]="true"
                      [disabled]="false"
                      [block]="false"
                      [href]="documentationLinks.onboarding.hotelXCertification"
                    ></xen-link>
                  </p>
                  <!-- Aina chat -->
                  <tgx-onboarding-aina-chat></tgx-onboarding-aina-chat>
                </div>
                <div class="col-7">
                  <br />
                  <div class="certification__steps">
                    <div class="certification__steps-status">
                      <div class="d-flex justify-content-between">
                        <p>{{ ((section.totalFilesOk / section.totalFiles) * 100).toFixed(0) }}% Completed</p>
                        <p>{{ section.totalFilesOk }} of {{ section.totalFiles }} files validated</p>
                      </div>
                    </div>
                    <div
                      class="progress m-0"
                      role="progressbar"
                      aria-label="cases_progressbar"
                      aria-valuenow="25"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      <div
                        [class]="
                          'progress-bar ' + (section.totalFiles === section.totalFilesOk ? 'progress-bar--success' : '')
                        "
                        [style.width]="
                          (section.totalFiles ? (section.totalFilesOk / section.totalFiles) * 100 : '0') + '%'
                        "
                      ></div>
                    </div>

                    <div class="row">
                      @for (stepFlow of section.stepsFlow; track $index) {
                        <div class="col">
                          <div class="certification__steps-step">
                            <p class="xen-heading-5 mb-0">{{ stepFlow.name }}</p>
                            <p class="description">{{ stepFlow.description }}</p>
                            <p class="description mb-0">Upload files:</p>
                            @for (fileInput of stepFlow.files; track $index) {
                              <div class="upload">
                                <p class="filename mb-1">
                                  <strong>{{ fileInput.label }}</strong> ({{ fileInput.file }})
                                </p>
                                <xen-btn
                                  [type]="'secondary'"
                                  [size]="'small'"
                                  [label]="
                                    fileInput.status === FileStatus.OK
                                      ? 'Validation success'
                                      : fileInput.status === FileStatus.ERROR
                                        ? 'Validation error'
                                        : 'Upload and verify'
                                  "
                                  [id]="'button id'"
                                  [full_width]="true"
                                  [icon]="true"
                                  [icon_placement]="fileInput.status === FileStatus.OK ? 'left' : 'right'"
                                  [icon_snippet]="fileInput.status === FileStatus.OK ? 'fal fa-check' : 'fal fa-upload'"
                                  [disabled]="false"
                                  [loading]="fileInput.status === FileStatus.IN_PROGRESS"
                                  [skin]="
                                    fileInput.status === FileStatus.OK
                                      ? 'green'
                                      : fileInput.status === FileStatus.ERROR
                                        ? 'red'
                                        : 'blue'
                                  "
                                  (clicked)="fileInput.status !== 'IN_PROGRESS' ? uploadFile.click() : null"
                                ></xen-btn>
                                <input
                                  type="file"
                                  style="display: none"
                                  id="file"
                                  accept=".json"
                                  (change)="onFileSelected($event, fileInput)"
                                  #uploadFile
                                />
                                @if (fileInput.status === FileStatus.ERROR) {
                                  <xen-link
                                    [size]="'small'"
                                    [weight]="'regular'"
                                    [label]="'View error description'"
                                    [icon]="true"
                                    [icon_snippet]="'fa-regular fa-eye'"
                                    [icon_placement]="'left'"
                                    [theme]="'red'"
                                    [underlined]="true"
                                    [disabled]="false"
                                    [block]="false"
                                    class="ms-2"
                                    [ngbPopover]="popError"
                                    placement="bottom"
                                    popoverClass="xen-popover"
                                    [popoverTitle]="'Error'"
                                    triggers="click"
                                    [autoClose]="true"
                                  ></xen-link>
                                }
                                <!-- Popover Error -->
                                <ng-template #popError>
                                  <div>
                                    <p>{{ fileInput.error }}</p>
                                  </div>
                                </ng-template>
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          <div class="d-flex align-items-center justify-content-end mb-5">
            <xen-btn
              [disabled]="!validateStep()"
              [type]="'primary'"
              [size]="'large'"
              [label]="'Certificate & continue'"
              [icon]="true"
              [icon_placement]="'right'"
              [icon_snippet]="'far fa-angle-right'"
              [skin]="'blue'"
              (clicked)="nextStep()"
            ></xen-btn>
            @if (i === 3) {
              <xen-link
                class="ms-3"
                [size]="'medium'"
                [weight]="'regular'"
                [label]="'Skip this step'"
                [icon]="true"
                [icon_snippet]="'fas fa-chevron-right'"
                [icon_placement]="'right'"
                [theme]="'blue'"
                [underlined]="true"
                [disabled]="false"
                [block]="false"
                (click)="openOnboardingValidationModal()"
              ></xen-link>
            }
          </div>
        </div>
      }
    }
  </div>
}

@if (actualStep === 4) {
  <div class="container">
    <!-- Congratulations -->
    <div class="section mt-5">
      <div class="row">
        <div class="col-7">
          <p class="section__title section__title--xl">Your company is ready to start using our platform</p>
          <p class="xen-heading-4">Congratulations!</p>
          <p class="section__subtit mb-5">
            You are now ready to activate your first live connection and start working in the production environment.
          </p>
          <p class="section__subtit mb-5">
            Our Onboarding team will be in touch shortly to schedule a walkthrough the platform, tools and website.
            Looking forward to connecting with you!
          </p>
          <xen-btn
            [type]="'primary'"
            [size]="'large'"
            [label]="'Start using Travelgate Platform'"
            [icon]="true"
            [icon_placement]="'right'"
            [icon_snippet]="'far fa-angle-right'"
            [skin]="'blue'"
            (click)="finishOnboarding()"
          ></xen-btn>
        </div>
        <div class="col-5">
          <img class="congrats" src="assets/img/pages/onboarding/congrats.svg" alt="Stamp onboarding" />
        </div>
      </div>
    </div>
  </div>
}
